import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Col, Form, Input, message, Tooltip, Spin } from 'antd';
import { DownloadOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useFileHandler } from '../../../hooks';
export const FormUploadFile = ({ id, name, label, isRequired = false, folderName = '', disabled = false, readOnly = false, rules = [], hidden = false, isRow = false, className = '', col = 24, responsive, value = null, onChange, accept = '.pdf,.doc,.docx' }) => {
    const { UploadFileServer, handleGetFile } = useFileHandler();
    const [uploadedFile, setUploadedFile] = useState(value);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    useEffect(() => {
        setUploadedFile(value ?? null);
    }, [value]);
    const colProps = responsive ?? { xs: col, sm: col, md: col, lg: col, xl: col, xxl: col };
    const validationRules = [
        ...(isRequired ? [{ required: true, message: `Trường ${label ?? 'này'} không được để trống!` }] : []),
        ...rules
    ];
    const toUploadFile = (file) => ({
        uid: `${Date.now()}-${file.name}`,
        originFileObj: file,
        name: file.name
    });
    const triggerChange = (file) => {
        onChange?.(file);
        if (!name) {
            setUploadedFile(file);
        }
    };
    const handleFileSelect = async (e) => {
        const files = e.target.files;
        if (!files?.length)
            return;
        if (!folderName) {
            message.error('FolderName chưa được thiết lập.');
            return;
        }
        try {
            setIsUploading(true);
            const uploadFiles = [toUploadFile(files[0])];
            const response = await UploadFileServer(uploadFiles, folderName);
            if (!response) {
                message.error('Upload thất bại: không có phản hồi từ server');
                triggerChange(null);
                return;
            }
            const uploadedData = response.data[0];
            setUploadedFile(uploadedData);
            triggerChange(uploadedData);
            message.success('Upload thành công!');
        }
        catch (err) {
            message.error('Upload thất bại: ' + (err.message || err));
            triggerChange(null);
        }
        finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };
    const handleDownload = () => {
        if (!uploadedFile)
            return;
        handleGetFile(uploadedFile.filePath, uploadedFile.originalName);
    };
    const handleEditStart = () => {
        if (!uploadedFile)
            return;
        setEditName(uploadedFile.originalName);
        setIsEditing(true);
    };
    const handleEditSave = () => {
        if (!uploadedFile)
            return;
        const updated = {
            ...uploadedFile,
            originalName: editName.trim() || uploadedFile.originalName
        };
        setUploadedFile(updated);
        triggerChange(updated);
        setIsEditing(false);
    };
    const handleEditCancel = () => {
        setIsEditing(false);
        setEditName('');
    };
    const fileDisplay = uploadedFile
        ? uploadedFile.size
            ? `${uploadedFile.originalName} (${(Number(uploadedFile.size) / 1024).toFixed(2)} KB)`
            : uploadedFile.originalName
        : '';
    const inputSuffix = isUploading ? (_jsx(Spin, { size: 'small', style: { marginRight: 8 } })) : (_jsx(_Fragment, { children: uploadedFile &&
            (isEditing ? (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: 'L\u01B0u t\u00EAn file', children: _jsx(SaveOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: (e) => {
                                e.stopPropagation();
                                if (!disabled && !readOnly)
                                    handleEditSave();
                            } }) }), _jsx(Tooltip, { title: 'H\u1EE7y', children: _jsx(CloseOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: (e) => {
                                e.stopPropagation();
                                if (!disabled && !readOnly)
                                    handleEditCancel();
                            } }) })] })) : (_jsxs(_Fragment, { children: [disabled || readOnly ? (_jsx(_Fragment, {})) : (_jsx(Tooltip, { title: '\u0110\u1ED5i t\u00EAn file', children: _jsx(EditOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: (e) => {
                                e.stopPropagation();
                                if (!disabled && !readOnly)
                                    handleEditStart();
                            } }) })), _jsx(Tooltip, { title: 'Download file', children: _jsx(DownloadOutlined, { style: { cursor: 'pointer', padding: 4 }, onClick: (e) => {
                                e.stopPropagation();
                                handleDownload();
                            } }) })] }))) }));
    const content = (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: isRow ? 'row' : 'column',
            alignItems: isRow ? 'center' : 'flex-start',
            gap: 8,
            width: '100%'
        }, children: [isRow && label && (_jsxs("label", { style: { whiteSpace: 'nowrap', fontWeight: 500, marginRight: 8 }, children: [label, isRequired && _jsx("span", { style: { color: 'red' }, children: " *" })] })), _jsx(Input, { readOnly: !isEditing || readOnly, placeholder: disabled || readOnly ? ' ' : 'Chọn file...', bordered: !readOnly, value: isEditing ? editName : fileDisplay, onChange: (e) => isEditing && setEditName(e.target.value), onClick: () => {
                    if (!disabled && !readOnly && !isEditing) {
                        document.getElementById(`hidden-file-input-${id}`)?.click();
                    }
                }, suffix: inputSuffix, style: {
                    color: '#000',
                    ...(isRow ? { flex: 1 } : {}) // nếu isRow thì thêm flex
                }, disabled: disabled || readOnly }), _jsx("input", { id: `hidden-file-input-${id}`, type: 'file', accept: accept, style: { display: 'none' }, onChange: handleFileSelect })] }));
    return (_jsx(Col, { ...colProps, children: name ? (_jsx(Form.Item, { label: !isRow ? label : undefined, name: name, rules: validationRules, hidden: hidden, className: className, children: content })) : (_jsxs("div", { className: className, style: { display: hidden ? 'none' : undefined }, children: [!isRow && label && (_jsxs("label", { style: { display: 'block', marginBottom: 4 }, children: [label, isRequired && _jsx("span", { style: { color: 'red' }, children: " *" })] })), content] })) }));
};
